import { DemandSourceRecord, IncludeExclude, AUTOCOMPLETE_GROUP_HEADER_PADDING, AUTOCOMPLETE_GROUP_ITEM_PADDING } from "../../../utils/types";
import Autocomplete from "../../library/Autocomplete";
import { Form, Button } from "react-bootstrap";
import { useState } from "react";
import './DemandSourcesAutocomplete.css'
import { isAllowedFilterField } from "../../../utils/UserHelper";
import { Menu, MenuItem, MenuProps } from "react-bootstrap-typeahead";

interface GroupedDemandSourceOption {
  id: string;
  demandSourceId: string;
  demandSourceName: string;
  demandSourcePartnerName: string;
  isGroup?: boolean;
  disabled?: boolean;
}

interface DemandSourcesAutocompleteProps {
  onChange: (selected: DemandSourceRecord[], isDirectSet?: boolean) => void;
  options: DemandSourceRecord[] | null;
  selected: DemandSourceRecord[];
  selectedForCheckboxes?: DemandSourceRecord[];
  reactRef: any;
  disabled?: boolean;
  multiple?: boolean;
  style?: {[key:string]:string};
  useIncludeExclude?: boolean;
  defaultIncludeExclude?: IncludeExclude;
  includeExcludeOnChange?: (newIncludeExclude:IncludeExclude) => void;
  useGrouped?: boolean;
  useCheckbox?: boolean;
}

function DemandSourcesAutocomplete(props: DemandSourcesAutocompleteProps) {
  const [arrowMode, setArrowMode] = useState(false);

  // Group demand sources by partner name (when useGrouped is true)
  const groupedOptions = (): GroupedDemandSourceOption[] => {
    // Handle null or undefined options
    if (!props.options || props.options.length === 0) {
      return [];
    }

    if (!props.useGrouped) {
      // Return flat list for backward compatibility
      return props.options.map(option => ({
        id: option.demandSourceId,
        demandSourceId: option.demandSourceId,
        demandSourceName: option.demandSourceName,
        demandSourcePartnerName: option.demandSourcePartnerName,
        isGroup: false,
        disabled: false
      }));
    }

    const groups: {[key: string]: DemandSourceRecord[]} = {};
    // Group by partner name
    props.options.forEach(option => {
      const groupKey = option.demandSourcePartnerName;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(option);
    });

    const result: GroupedDemandSourceOption[] = [];
    
    // Create grouped structure
    Object.keys(groups).sort().forEach(groupKey => {
      const groupItems = groups[groupKey];
      
      // Add group header
      result.push({
        id: `group_${groupKey}`,
        demandSourceId: `group_${groupKey}`,
        demandSourceName: groupKey,
        demandSourcePartnerName: groupKey,
        isGroup: true,
        disabled: false
      });
      
      // Add group members (sorted by name)
      groupItems.sort((a, b) => a.demandSourceName.localeCompare(b.demandSourceName)).forEach(item => {
        result.push({
          id: item.demandSourceId,
          demandSourceId: item.demandSourceId,
          demandSourceName: item.demandSourceName,
          demandSourcePartnerName: item.demandSourcePartnerName,
          isGroup: false,
          disabled: false
        });
      });
    });
    
    return result;
  };

  const labelKeyFunction = (option: GroupedDemandSourceOption | DemandSourceRecord) => {
    if ('isGroup' in option && option.isGroup) {
      return option.demandSourcePartnerName;
    }
    return option.demandSourcePartnerName + " - " + option.demandSourceName;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!props.useGrouped) return; // Only for grouped mode
    
    switch(e.code) {
      case "Enter":
        const activeIndex = props.reactRef?.current?.state?.activeIndex;
        props.reactRef?.current?.toggleMenu();
        if (props.reactRef?.current?.setState) {
          props.reactRef.current.setState({...props.reactRef.current.state, activeIndex: activeIndex});
        }
        break;
      case "ArrowDown":
      case "ArrowUp":
        setArrowMode(true);
        break;
      case "Space":
        if (arrowMode && props.reactRef?.current?.state?.activeItem) {
          e.preventDefault();
          handleClick(e, props.reactRef.current.state.activeItem);
        }
        break;
      default:
        setArrowMode(false);
        break;
    }
  };

  const handleClick = (e: any, selectedValue: GroupedDemandSourceOption, currentlyVisibleResults?: GroupedDemandSourceOption[]) => {
    e.stopPropagation();

    if (selectedValue.isGroup) {
      // For group selection, only toggle items that are CURRENTLY VISIBLE in the filtered results
      const groupKey = selectedValue.demandSourcePartnerName;

      // Get only the visible items for this group (respects current filter)
      const visibleGroupItems = currentlyVisibleResults
        ? currentlyVisibleResults.filter(opt => !opt.isGroup && opt.demandSourcePartnerName === groupKey)
        : groupedOptions().filter(opt => !opt.isGroup && opt.demandSourcePartnerName === groupKey);

      // Check if ALL visible group items are currently selected
      const selectedGroupItems = visibleGroupItems.filter(item =>
        props.selectedForCheckboxes?.some(selected => selected.demandSourceId === item.demandSourceId)
      );
      const isGroupFullySelected = selectedGroupItems.length === visibleGroupItems.length && visibleGroupItems.length > 0;

      if (isGroupFullySelected) {
        // If all visible items are selected, unselect all visible items (toggle off)
        const groupRecords = visibleGroupItems.map(item => ({
          demandSourceId: item.demandSourceId,
          demandSourceName: item.demandSourceName,
          demandSourcePartnerName: item.demandSourcePartnerName
        }));
        props.onChange(groupRecords, false);
      } else {
        // If not all visible items are selected, select all unselected visible items
        const unselectedGroupItems = visibleGroupItems.filter(item =>
          !props.selectedForCheckboxes?.some(selected => selected.demandSourceId === item.demandSourceId)
        );

        const unselectedRecords = unselectedGroupItems.map(item => ({
          demandSourceId: item.demandSourceId,
          demandSourceName: item.demandSourceName,
          demandSourcePartnerName: item.demandSourcePartnerName
        }));

        props.onChange(unselectedRecords, false);
      }
    } else {
      // For individual items, pass just the clicked item to onChange
      const demandSourceRecord = {
        demandSourceId: selectedValue.demandSourceId,
        demandSourceName: selectedValue.demandSourceName,
        demandSourcePartnerName: selectedValue.demandSourcePartnerName
      };

      props.onChange([demandSourceRecord], false);
    }
  };

  const renderMenuItemChildren = (option: GroupedDemandSourceOption, obj: any, index: number, currentlyVisibleResults?: GroupedDemandSourceOption[]) => {
    const isChecked = props.selectedForCheckboxes?.some(selected => selected.demandSourceId === option.demandSourceId) || false;

    if (option.isGroup) {
      // Group header - check if all VISIBLE members are selected
      const visibleGroupItems = currentlyVisibleResults
        ? currentlyVisibleResults.filter(opt => !opt.isGroup && opt.demandSourcePartnerName === option.demandSourcePartnerName)
        : groupedOptions().filter(opt => !opt.isGroup && opt.demandSourcePartnerName === option.demandSourcePartnerName);

      const selectedGroupItems = visibleGroupItems.filter(item =>
        props.selectedForCheckboxes?.some(selected => selected.demandSourceId === item.demandSourceId)
      );

      // Determine checkbox state: checked, indeterminate, or unchecked
      let groupCheckState: boolean | "indeterminate" = false;
      if (selectedGroupItems.length === visibleGroupItems.length && visibleGroupItems.length > 0) {
        groupCheckState = true;
      } else if (selectedGroupItems.length > 0) {
        groupCheckState = "indeterminate";
      }

      const checkboxElement = (
        <Form.Check
          key={option.id + '-' + groupCheckState.toString()}
          inline
          readOnly
          style={{ whiteSpace: "nowrap", margin: "0 5px", cursor: "pointer" }}
          type="checkbox"
          disabled={option.disabled}
          checked={groupCheckState === true}
          ref={(input: any) => {
            if (input && groupCheckState === "indeterminate") {
              input.indeterminate = true;
            }
          }}
          onClick={(e: any) => {
            e.stopPropagation(); // Prevent triggering row click
            handleClick(e, option, currentlyVisibleResults);
          }}
        />
      );

      return (
        <div
          style={{
            padding: AUTOCOMPLETE_GROUP_HEADER_PADDING,
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#f1f3f4",
            borderBottom: "1px solid #e9ecef",
            cursor: "pointer"
          }}
          onClick={(e: any) => {
            e.stopPropagation();
            handleClick(e, option, currentlyVisibleResults);
          }}
        >
          <strong style={{ display: "flex" }}>
            {checkboxElement}
            {option.demandSourcePartnerName}
          </strong>
        </div>
      );
    } else {
      // Group member - indented
      const checkboxElement = (
        <Form.Check
          key={option.id + '-' + isChecked.toString()}
          inline
          readOnly
          style={{ whiteSpace: "nowrap", margin: "0 5px", cursor: "pointer" }}
          type="checkbox"
          disabled={option.disabled}
          checked={isChecked}
          onClick={(e: any) => {
            e.stopPropagation(); // Prevent triggering row click
            handleClick(e, option);
          }}
        />
      );

      return (
        <div
          style={{
            padding: AUTOCOMPLETE_GROUP_ITEM_PADDING,
            display: "flex",
            justifyContent: "space-between",
            cursor: "pointer"
          }}
          onClick={(e: any) => {
            e.stopPropagation();
            handleClick(e, option);
          }}
        >
          <span style={{ display: "flex" }}>
            {checkboxElement}
            {option.demandSourceName}
          </span>
          <span style={{ float: "right", color: "#717171", fontFamily: "monospace" }}>
            {option.demandSourceId}
          </span>
        </div>
      );
    }
  };

  const filterOptions = (option: GroupedDemandSourceOption, filterProps: any) => {
    if (props.useGrouped) {
      const searchText = filterProps.text?.toLowerCase() || '';

      // If no search text, show all
      if (!searchText) {
        return true;
      }

      // For groups, only show if at least one child matches
      if (option.isGroup) {
        const groupKey = option.demandSourcePartnerName;
        const hasMatchingChildren = groupedOptions().some(opt =>
          !opt.isGroup &&
          opt.demandSourcePartnerName === groupKey &&
          (opt.demandSourceName.toLowerCase().includes(searchText) ||
           opt.demandSourcePartnerName.toLowerCase().includes(searchText) ||
           opt.demandSourceId.toLowerCase().includes(searchText))
        );
        return hasMatchingChildren;
      }

      // For individual items, check both demand source name and partner name
      return option.demandSourceName.toLowerCase().includes(searchText) ||
             option.demandSourcePartnerName.toLowerCase().includes(searchText) ||
             option.demandSourceId.toLowerCase().includes(searchText);
    }
    return undefined; // Use default filtering for non-grouped
  };

  const handleRegularOnChange = (selected: any[]) => {
    if (props.useGrouped) {
      // When clicking on an already selected item, the autocomplete adds it again
      // We need to detect if the "new" selection contains a duplicate
      const selectedIds = selected.map(s => s.demandSourceId);
      const hasDuplicate = selectedIds.length !== new Set(selectedIds).size;

      // If there's a duplicate, it means user clicked on an already selected item
      if (hasDuplicate || (selected.length > 0 && selected[selected.length - 1] &&
          props.selected.some(p => p.demandSourceId === selected[selected.length - 1].demandSourceId))) {

        // Find the item that was clicked (the duplicate or last item)
        const clickedItem = selected[selected.length - 1];

        // Toggle just this one item
        const clickedRecord = {
          demandSourceId: clickedItem.demandSourceId,
          demandSourceName: clickedItem.demandSourceName,
          demandSourcePartnerName: clickedItem.demandSourcePartnerName
        };
        props.onChange([clickedRecord], false);
        return;
      }

      // Check if this is likely an X button action (items were removed)
      const isRemoval = selected.length < props.selected.length;

      // Check if this is a single item removal (clicking on a selected item to deselect)
      if (isRemoval && props.selected.length - selected.length === 1) {
        // Find which item was removed
        const removedItem = props.selected.find(item =>
          !selected.some(s => s.demandSourceId === item.demandSourceId)
        );

        if (removedItem) {
          // Pass just the removed item to trigger toggle behavior
          // IMPORTANT: Pass false for isDirectSet to trigger toggle, not replacement
          props.onChange([removedItem], false);
          return;
        }
      }

      // Check if a group was selected
      const newlySelected = selected.filter(item =>
        !props.selected?.some(existing => existing.demandSourceId === item.demandSourceId)
      );

      // Check if any newly selected item is a group
      const selectedGroup = newlySelected.find(item => item.isGroup);

      if (selectedGroup) {
        // Handle group selection (from row click)
        const groupKey = selectedGroup.demandSourcePartnerName;
        const groupItems = groupedOptions().filter(opt =>
          !opt.isGroup && opt.demandSourcePartnerName === groupKey
        );

        // Check if ALL group items are currently selected
        const selectedGroupItems = groupItems.filter(item =>
          props.selectedForCheckboxes?.some(selected => selected.demandSourceId === item.demandSourceId)
        );
        const isGroupFullySelected = selectedGroupItems.length === groupItems.length && groupItems.length > 0;

        if (isGroupFullySelected) {
          // If all items are selected, unselect all (toggle off)
          const groupRecords = groupItems.map(item => ({
            demandSourceId: item.demandSourceId,
            demandSourceName: item.demandSourceName,
            demandSourcePartnerName: item.demandSourcePartnerName
          }));
          props.onChange(groupRecords, false);
        } else {
          // If not all items are selected, select all remaining items
          const unselectedGroupItems = groupItems.filter(item =>
            !props.selectedForCheckboxes?.some(selected => selected.demandSourceId === item.demandSourceId)
          );

          const unselectedRecords = unselectedGroupItems.map(item => ({
            demandSourceId: item.demandSourceId,
            demandSourceName: item.demandSourceName,
            demandSourcePartnerName: item.demandSourcePartnerName
          }));

          props.onChange(unselectedRecords, false);
        }
        return;
      }

      // If exactly one new item was selected - whether from empty state or not,
      // it's a row click on an individual item - just toggle that one item
      if (newlySelected.length === 1 && !newlySelected[0].isGroup) {
        const clickedItem = newlySelected[0];
        const clickedRecord = {
          demandSourceId: clickedItem.demandSourceId,
          demandSourceName: clickedItem.demandSourceName,
          demandSourcePartnerName: clickedItem.demandSourcePartnerName
        };
        props.onChange([clickedRecord], false);
        return;
      }

      // Convert GroupedDemandSourceOption[] back to DemandSourceRecord[]
      const demandSourceRecords = selected.filter(item => !item.isGroup).map(item => ({
        demandSourceId: item.demandSourceId,
        demandSourceName: item.demandSourceName,
        demandSourcePartnerName: item.demandSourcePartnerName
      }));

      // Only pass true for isDirectSet when it's a bulk clear (X button on input)
      // which is when selected is empty or it's a multi-item removal
      const isDirectSet = selected.length === 0 || (isRemoval && props.selected.length - selected.length > 1);
      props.onChange(demandSourceRecords, isDirectSet);
    } else {
      // For non-grouped, pass through as-is
      props.onChange(selected, false);
    }
  };

  const handleDeselectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Clear all selections by setting to empty array with isDirectSet=true
    props.onChange([], true);
  };

  const renderMenu = (results: any[], menuProps: MenuProps) => {
    if (!props.useGrouped || !props.useCheckbox) {
      return <Menu {...menuProps}>{results.map((result, index) => (
        <MenuItem key={index} option={result} position={index}>
          {result}
        </MenuItem>
      ))}</Menu>;
    }

    const handleSelectAll = (e: React.MouseEvent) => {
      e.stopPropagation();

      // Get only non-group items from the filtered results
      const filteredItems = results.filter((opt: GroupedDemandSourceOption) => !opt.isGroup);

      // Find unselected items from the filtered results
      const unselectedItems = filteredItems.filter((item: GroupedDemandSourceOption) =>
        !props.selectedForCheckboxes?.some(selected => selected.demandSourceId === item.demandSourceId)
      );

      if (unselectedItems.length > 0) {
        const records = unselectedItems.map((item: GroupedDemandSourceOption) => ({
          demandSourceId: item.demandSourceId,
          demandSourceName: item.demandSourceName,
          demandSourcePartnerName: item.demandSourcePartnerName
        }));
        props.onChange(records, false);
      }
    };

    return (
      <Menu {...menuProps}>
        <div style={{
          display: "flex",
          gap: "0.5rem",
          padding: "0 0.5rem 0.5rem 0.5rem",
          borderBottom: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa"
        }}>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleSelectAll}
            style={{ flex: 1, fontSize: "0.875rem" }}
          >
            Select All
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleDeselectAll}
            style={{ flex: 1, fontSize: "0.875rem" }}
          >
            Deselect All
          </Button>
        </div>
        {results.map((result, index) => (
          <MenuItem key={index} option={result} position={index}>
            {renderMenuItemChildren(result, menuProps, index, results)}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  return (
    <Autocomplete
      id="demandSourcesAutocomplete"
      className={props.useGrouped ? 'demand-sources-grouped' : 'demand-sources'}
      renderMenu={props.useGrouped && props.useCheckbox ? renderMenu : undefined}
      renderMenuItemChildren={props.useGrouped && props.useCheckbox ? renderMenuItemChildren : undefined}
      placeholder="Demand Source"
      reactKey="demandSourcesAutocomplete"
      labelKey={labelKeyFunction}
      onChange={handleRegularOnChange}
      onKeyDown={props.useGrouped ? handleKeyDown : undefined}
      options={groupedOptions()}
      selected={props.useGrouped ? props.selected?.map(ds => ({
        id: ds.demandSourceId,
        demandSourceId: ds.demandSourceId,
        demandSourceName: ds.demandSourceName,
        demandSourcePartnerName: ds.demandSourcePartnerName,
        isGroup: false,
        disabled: false
      })) : props.selected}
      filterBy={props.useGrouped ? filterOptions : undefined}
      reactRef={props.reactRef}
      disabled={props.disabled}
      useIncludeExclude={props.useIncludeExclude && isAllowedFilterField("demandSourcesExclude")}
      defaultIncludeExclude={props.defaultIncludeExclude}
      includeExcludeOnChange={props.includeExcludeOnChange}
      multiple={props.multiple}
      style={props.style}
      maxDisplayTokens={5}
      keepDropdownOpen={!props.useGrouped}
    />
  );
}

export default DemandSourcesAutocomplete;